import { isFeCode } from "../../lib/http/errors";
import type { FeCode } from "../../lib/http/errors";

/**
 * 런치 프래그먼트 — 제어 평면 LTI 런치가 끝나면 `${shell}/launch#ticket=<t>` 또는 `#error=FE-304`
 * 로 보낸다. 문 B(Campus BFF SSO)는 `${shell}/sso#bffToken=<t>` 로도 들어올 수 있다.
 *
 * 프래그먼트는 서버 로그·Referer 에 남지 않지만 주소창·방문 기록에는 남는다. 그래서 앱이 그려지기 전에
 * 한 번 읽고 history.replaceState 로 즉시 지운다(일회용 티켓 재사용·어깨너머 노출 방지).
 */
export interface LaunchFragment {
  ticket?: string;
  bffToken?: string;
  error?: FeCode;
  /**
   * 4.6 §4.3 — 들어온 뒤 갈 곳(`/sso#bffToken=…&next=course` — Campus ST5 'Field 에서 미션 하기' · `next=microbit` — micro:bit 놀이터 미션 · `next=stages` — Campus 운영 콘솔 → 무대 꾸밈(운영자만)). 아는 값만 받는다 —
   * 모르는 값은 버린다(열린 리다이렉트 방지 · 경로가 아니라 이름).
   */
  next?: LaunchNext;
}

/** 런치 뒤 바로 갈 화면 이름(허용 목록) */
export type LaunchNext = "course" | "microbit" | "stages";
const NEXTS: readonly LaunchNext[] = ["course", "microbit", "stages"];
export const isLaunchNext = (v: unknown): v is LaunchNext =>
  typeof v === "string" && (NEXTS as readonly string[]).includes(v);

const KEYS = ["ticket", "bffToken", "error"] as const;

export function parseLaunchFragment(hash: string): LaunchFragment {
  const q = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const out: LaunchFragment = {};
  const ticket = q.get("ticket");
  const bffToken = q.get("bffToken");
  const error = q.get("error");
  if (ticket) out.ticket = ticket;
  if (bffToken) out.bffToken = bffToken;
  const next = q.get("next");
  if (isLaunchNext(next)) out.next = next;
  // 모르는 오류 코드는 세션 오류(FE-304)로 본다 — 학생에게는 '다시 열기' 안내가 가장 안전하다.
  if (error != null) out.error = isFeCode(error) ? error : "FE-304";
  return out;
}

/** 프래그먼트를 읽고 주소에서 지운다. 런치 키가 없으면 주소를 건드리지 않는다. */
export function consumeLaunchFragment(
  loc: Pick<Location, "hash" | "pathname" | "search"> = window.location,
  hist: Pick<History, "replaceState" | "state"> = window.history,
): LaunchFragment {
  const hash = loc.hash;
  if (!hash || hash === "#") return {};
  const q = new URLSearchParams(hash.slice(1));
  if (!KEYS.some((k) => q.has(k))) return {};
  const frag = parseLaunchFragment(hash);
  hist.replaceState(hist.state, "", `${loc.pathname}${loc.search}`);
  return frag;
}
